import { translate, translateWithVars } from "shared/languages";
import { TableSortState } from "shared/types/tableSort";

// Per-report sort metadata: which columns are sortable in the UI, their
// label, and their comparator type. The comparators MUST match the AntD
// column sorters (numeric subtraction, localeCompare for names, ISO string
// order for dates) so the PDF shows exactly the view the admin composed.
export type SortMeta = Record<
  string,
  {
    label: string;
    // "date-future" sorts identically to "date" but words its direction for
    // forward-looking dates (expiring soon): "terbaru lebih dulu" would
    // misread there, since descending means the furthest expiry first.
    type: "number" | "text" | "date" | "date-future";
    // Optional accessor when the sortable value differs from the raw field
    // (e.g. Performa Kelas sorts private rows' fill rate below any value).
    value?: (row: any) => unknown;
  }
>;

const direction = (
  type: "number" | "text" | "date" | "date-future",
  order: "ascend" | "descend",
) => {
  if (type === "number")
    return translate(order === "descend" ? "pdf.dir_number_desc" : "pdf.dir_number_asc");
  if (type === "date")
    return translate(order === "descend" ? "pdf.dir_date_desc" : "pdf.dir_date_asc");
  if (type === "date-future")
    return translate(order === "descend" ? "pdf.dir_date_future_desc" : "pdf.dir_date_future_asc");
  return order === "descend" ? "Z–A" : "A–Z";
};

export const sortCaption = (
  meta: SortMeta,
  sortState: TableSortState,
  fallback: { key: string; order: "ascend" | "descend" },
) => {
  const active = sortState && meta[sortState.key] ? sortState : fallback;
  const m = meta[active.key];
  if (!m) return undefined;
  return translateWithVars("pdf.sorted_by", { label: m.label, direction: direction(m.type, active.order) });
};

// Returns a NEW array sorted by the active sort; a cleared/unknown sort
// falls back to the server's default order (rows as delivered).
export const sortRows = <Row extends Record<string, any>>(
  rows: Row[],
  meta: SortMeta,
  sortState: TableSortState,
): Row[] => {
  if (!sortState || !meta[sortState.key]) return rows;
  const { key, order } = sortState;
  const type = meta[key].type;
  const accessor = meta[key].value ?? ((row: Row) => row[key]);
  const compare = (a: Row, b: Row) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av == null && bv == null) return 0;
    if (av == null) return -1;
    if (bv == null) return 1;
    if (type === "number") return Number(av) - Number(bv);
    return String(av).localeCompare(String(bv));
  };
  // Descending NEGATES the comparator rather than reversing the sorted array.
  // Both put the same values in the same order, but only this one keeps TIED
  // rows in the order the server sent them, which is what AntD does on screen
  // (its sort is stable in both directions). Reversing flipped every tie
  // group, so a descending sort printed the tied rows backwards against the
  // table the admin was looking at. Invisible while sorts were on columns of
  // mostly distinct values (money, dates); routine on a count column like
  // Upgrade, where nearly every row is 0 and therefore tied.
  // Null ordering is unchanged: nulls first ascending, last descending.
  const direction = order === "descend" ? -1 : 1;
  return [...rows].sort((a, b) => direction * compare(a, b));
};

// Page chunking by weight, used by the payslip (its own hand-measured
// budgets). The Laporan reports plan their pages with ../pagePlan instead,
// which measures the real text. `weight` says how many single-line rows a
// row is worth, and each page takes rows until the next one would push it
// past its budget. Never capped by a page count: the caller has already
// limited the rows, and capping pages here would silently drop the tail.
export const chunkRowsWeighted = <Row,>(
  rows: Row[],
  firstPageRows: number,
  restPageRows: number,
  weight: (row: Row) => number,
): Row[][] => {
  const chunks: Row[][] = [[]];
  let used = 0;
  for (const row of rows) {
    const w = weight(row);
    const budget = chunks.length === 1 ? firstPageRows : restPageRows;
    // A page always takes at least one row, so an outsized row cannot loop.
    if (used + w > budget && chunks[chunks.length - 1].length > 0) {
      chunks.push([]);
      used = 0;
    }
    chunks[chunks.length - 1].push(row);
    used += w;
  }
  return chunks;
};
